<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, RouterView, useRoute } from 'vue-router'
import { Button } from '../components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'
import { useAuth } from '../composables/useAuth'

const { user, logout } = useAuth()
const route = useRoute()
const isRatingLinkRoute = computed(() => route.path === '/test-ground/rating')
const isResultsRoute = computed(() => route.path.startsWith('/results'))
</script>

<template>
  <div class="h-screen flex flex-col bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
    <header
      v-if="!isRatingLinkRoute"
      class="h-12 shrink-0 border-b border-gray-200 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur"
    >
      <div class="h-full px-4 flex items-center justify-between">
        <div class="flex items-center gap-4" :class="{ 'results-navigation': isResultsRoute }">
          <RouterLink to="/test-ground" class="flex items-center gap-2 hover:opacity-80">
            <div class="i-lucide-activity text-indigo-500 text-lg" />
            <span class="font-semibold text-sm">模型评测</span>
          </RouterLink>
          <RouterLink
            to="/test-ground"
            class="text-xs text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
            active-class="text-indigo-600 dark:text-indigo-400"
          >
            构建评测
          </RouterLink>
          <RouterLink
            to="/test-ground-ng"
            class="text-xs text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
            active-class="text-indigo-600 dark:text-indigo-400"
            title="下一代架构（SMPL-X + StyleMLP）评测，与构建评测各自独立"
          >
            下一代架构
          </RouterLink>
          <RouterLink
            to="/evaluation-assets"
            class="text-xs text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
            active-class="text-indigo-600 dark:text-indigo-400"
          >
            评测资产
          </RouterLink>
          <RouterLink
            to="/evaluation-management"
            class="text-xs text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
            active-class="text-indigo-600 dark:text-indigo-400"
          >
            评测任务
          </RouterLink>
          <RouterLink
            to="/results"
            class="text-xs text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
            active-class="text-indigo-600 dark:text-indigo-400"
          >
            评测结果
          </RouterLink>
          <RouterLink
            to="/video-eval"
            class="text-xs text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
            active-class="text-indigo-600 dark:text-indigo-400"
          >
            视频评测
          </RouterLink>
          <RouterLink
            to="/tracker-fit"
            class="text-xs text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
            active-class="text-indigo-600 dark:text-indigo-400"
          >
            Tracker 评测
          </RouterLink>
          <RouterLink
            to="/changelog"
            class="text-xs text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
            active-class="text-indigo-600 dark:text-indigo-400"
          >
            更新日志
          </RouterLink>
          <div class="h-4 w-px bg-gray-200 dark:bg-gray-800" />
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <Button
                variant="ghost"
                size="xs"
                class="shrink-0 text-xs text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
              >
                <div class="i-lucide-wrench text-xs" />
                内部工具
                <div class="i-lucide-chevron-down text-[10px]" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" class="w-56">
              <DropdownMenuLabel class="flex items-center gap-2 text-[11px] text-gray-500">
                <div class="i-lucide-lock-keyhole text-xs" />
                内部使用
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem as-child>
                <RouterLink to="/export" class="flex w-full items-center gap-2">
                  <div class="i-lucide-file-down text-indigo-500" />
                  <div class="min-w-0">
                    <div>PB 导出工具</div>
                    <div class="text-[11px] text-gray-500">Idle / Speaking</div>
                  </div>
                </RouterLink>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div v-if="user" class="flex items-center gap-3" :class="{ 'results-user': isResultsRoute }">
          <img
            v-if="user.avatarUrl"
            :src="user.avatarUrl"
            :alt="user.name"
            class="w-6 h-6 rounded-full"
          >
          <span class="text-xs text-gray-600 dark:text-gray-400">{{ user.name }}</span>
          <button
            class="text-xs text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
            @click="logout"
          >
            登出
          </button>
        </div>
      </div>
    </header>
    <main class="flex-1 min-h-0 overflow-hidden">
      <RouterView />
    </main>
  </div>
</template>

<style scoped>
.results-navigation{min-width:0;flex:1;overflow-x:auto;white-space:nowrap}
.results-navigation>a{flex-shrink:0}
.results-user{flex-shrink:0;white-space:nowrap;margin-left:12px}
</style>
